import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip,
  TextField, List, ListItem, ListItemText, CircularProgress, Alert, Divider,
  IconButton, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import * as eqpAgentApi from '../../lib/api/eqpAgent';
import {
  STATUS_LABEL, STATUS_COLOR, isPolling, describeResult, canRegister, canCancel, formatProgress,
} from './eqpDiscovery';
import { useCloseOnBack } from '../../hooks/useCloseOnBack';

const POLL_MS = 3000;

/**
 * 주변 장비 찾기 다이얼로그 - EqpAgentDetailDialog 에서 [주변 장비 찾기]를 누르면 열린다.
 * 열리는 즉시 검색 요청을 하나 만들고(기본 대역), 3초마다 상태/결과를 폴링한다.
 * "검색 대역 직접 입력"으로 다시 검색하면 그 대역으로 새 요청을 만든다.
 */
const EqpAgentDiscoveryDialog = ({ open, agentUuid, agentNm, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  useCloseOnBack(open, onClose);
  const [request, setRequest] = useState(null); // {reqUuid, status, results, ...}
  const [starting, setStarting] = useState(false);
  const [subnetsInput, setSubnetsInput] = useState('');
  const [confirmId, setConfirmId] = useState(null); // 인라인 확인 중인 resultId
  const [registeringId, setRegisteringId] = useState(null);
  const [cancelConfirming, setCancelConfirming] = useState(false); // [중지] 인라인 확인 중
  const [cancelling, setCancelling] = useState(false);

  const statusRef = useRef(null);
  const timerRef = useRef(null);

  // force=true면 요청 상태가 이미 끝난(DONE 등) 뒤에도 다시 조회한다 - [등록] 직후처럼
  // "결과 안의 한 행"만 바뀌어서 요청 자체의 status는 안 바뀌는 경우, 평소 폴링(진행중일 때만)으로는
  // 그 변경이 화면에 반영되지 않는다(2026-10-17 신고: 등록해도 버튼이 그대로 남아 있음).
  const poll = useCallback(async (force) => {
    if (!agentUuid || (!force && !isPolling(statusRef.current))) return;
    try {
      const res = await eqpAgentApi.getLatestAgentDiscovery(agentUuid);
      const info = eqpAgentApi.extractDiscovery(res);
      if (info) {
        statusRef.current = info.status;
        setRequest(info);
      }
    } catch (e) {
      // 폴링 실패는 조용히 - 다음 주기에 다시 시도
    }
  }, [agentUuid]);

  const startSearch = useCallback(async (subnetsOverride) => {
    if (!agentUuid) return;
    setStarting(true);
    setConfirmId(null);
    try {
      const body = subnetsOverride ? { subnets: subnetsOverride } : {};
      const res = await eqpAgentApi.startAgentDiscovery(agentUuid, body);
      const info = eqpAgentApi.extractDiscovery(res);
      statusRef.current = info?.status || 'REQUESTED';
      setRequest(info);
    } catch (e) {
      const msg = e?.response?.data?.message || '대역 입력이 올바른지 확인하세요(예: 192.168.0.0/24).';
      enqueueSnackbar(`검색을 시작하지 못했습니다. ${msg}`, { variant: 'error' });
    } finally {
      setStarting(false);
    }
  }, [agentUuid, enqueueSnackbar]);

  useEffect(() => {
    if (!open) return undefined;
    setRequest(null);
    statusRef.current = 'REQUESTED';
    setSubnetsInput('');
    setConfirmId(null);
    setCancelConfirming(false);
    startSearch(); // 버튼을 눌러 다이얼로그를 여는 순간 바로 검색 시작(기본 대역)

    timerRef.current = setInterval(poll, POLL_MS);
    return () => {
      clearInterval(timerRef.current);
      timerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agentUuid]);

  if (!open) return null;

  const results = request?.results || [];
  const status = request?.status;

  const handleCancel = async () => {
    if (!request?.reqUuid) return;
    setCancelling(true);
    try {
      const res = await eqpAgentApi.cancelAgentDiscovery(agentUuid, request.reqUuid);
      const info = eqpAgentApi.extractDiscovery(res);
      if (info) {
        statusRef.current = info.status;
        setRequest(info);
      }
      setCancelConfirming(false);
    } catch (e) {
      enqueueSnackbar('중지하지 못했습니다. 잠시 후 다시 시도하세요.', { variant: 'error' });
    } finally {
      setCancelling(false);
    }
  };

  const handleRegister = async (resultId) => {
    if (registeringId) return; // 이미 등록 요청이 나가 있으면 또 누르지 못하게(더블클릭 방지)
    setRegisteringId(resultId);
    try {
      const res = await eqpAgentApi.registerDiscoveryCandidate(agentUuid, request.reqUuid, resultId);
      const createdEqp = res?.data?.info; // EqpInfDto - eqpUuid/eqpNm
      enqueueSnackbar('장비로 등록했습니다.', { variant: 'success' });
      setConfirmId(null);
      // 서버가 돌려준 장비로 그 행을 바로 "등록됨"으로 바꿔 둔다(버튼이 안 사라지는 문제의 1차 방어) -
      // 뒤이은 poll(true)가 권위 있는 값(matchedEqpNm 조인 등)으로 덮어쓴다.
      setRequest((prev) => (prev ? {
        ...prev,
        results: (prev.results || []).map((row) => (row.resultId === resultId ? {
          ...row,
          matchedEqpUuid: createdEqp?.eqpUuid || row.matchedEqpUuid || 'pending',
          matchedEqpNm: createdEqp?.eqpNm ?? row.matchedEqpNm,
        } : row)),
      } : prev));
      poll(true); // 요청 status는 그대로(DONE 등)라도 결과 행의 등록 여부는 강제로 다시 가져온다
    } catch (e) {
      if (e?.response?.status === 409) {
        // 이미 다른 요청(더블클릭/다른 탭)이 먼저 등록했다 - 내가 중복 생성한 건 아니니 조용히 최신 상태만 맞춘다.
        enqueueSnackbar('이미 등록된 후보입니다.', { variant: 'info' });
        poll(true);
      } else {
        enqueueSnackbar('등록에 실패했습니다.', { variant: 'error' });
      }
    } finally {
      setRegisteringId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>{agentNm ? `${agentNm} - 주변 장비 찾기` : '주변 장비 찾기'}</Box>
        <IconButton aria-label="닫기" onClick={onClose} size="small" sx={{ flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          에이전트 PC가 있는 사내망에서 장비를 찾습니다. 고객사 전산 담당자 동의 후 사용하세요.
        </Alert>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">상태</Typography>
          {status ? (
            <Chip size="small" color={STATUS_COLOR[status] || 'default'} label={STATUS_LABEL[status] || status} />
          ) : (
            <CircularProgress size={16} />
          )}
          {isPolling(status) && <CircularProgress size={16} />}
          {status === 'RUNNING' && formatProgress(request) && (
            <Typography variant="caption" color="text.secondary">{formatProgress(request)}</Typography>
          )}
          {request?.errMsg && (
            <Typography variant="caption" color="error.main">{request.errMsg}</Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {canCancel(status) && !cancelConfirming && (
            <Button size="small" color="warning" variant="outlined" onClick={() => setCancelConfirming(true)}>
              중지
            </Button>
          )}
          {canCancel(status) && cancelConfirming && (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">검색을 중지할까요?</Typography>
              <Button size="small" onClick={() => setCancelConfirming(false)} disabled={cancelling}>
                취소
              </Button>
              <Button
                size="small" variant="contained" color="warning"
                onClick={handleCancel}
                disabled={cancelling}
                startIcon={cancelling ? <CircularProgress size={14} color="inherit" /> : null}
              >
                중지 확인
              </Button>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" gutterBottom>검색 결과</Typography>
        {!request ? (
          <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={22} /></Box>
        ) : results.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {isPolling(status) ? '아직 찾은 장비가 없습니다(검색 중...).' : '찾은 장비가 없습니다.'}
          </Typography>
        ) : (
          <List dense disablePadding>
            {results.map((row) => {
              const key = row.resultId;
              const registerable = canRegister(row);
              const confirming = confirmId === key;
              const registering = registeringId === key;
              return (
                <ListItem
                  key={key}
                  disableGutters
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', py: 0.75 }}
                >
                  <ListItemText
                    sx={{ flex: '1 1 200px', m: 0 }}
                    primary={`${row.ip}:${row.port}`}
                    secondary={describeResult(row)}
                  />
                  {row.matchedEqpUuid && (
                    <Chip size="small" color="success" variant="outlined"
                      label={`이미 등록됨${row.matchedEqpNm ? `: ${row.matchedEqpNm}` : ''}`} />
                  )}
                  {registerable && !confirming && (
                    <Button size="small" variant="outlined" onClick={() => setConfirmId(key)}>
                      등록
                    </Button>
                  )}
                  {registerable && confirming && (
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">장비로 등록할까요?</Typography>
                      <Button size="small" onClick={() => setConfirmId(null)} disabled={registering}>
                        취소
                      </Button>
                      <Button
                        size="small" variant="contained" color="primary"
                        onClick={() => handleRegister(key)}
                        disabled={registering}
                        startIcon={registering ? <CircularProgress size={14} color="inherit" /> : null}
                      >
                        확인
                      </Button>
                    </Box>
                  )}
                </ListItem>
              );
            })}
          </List>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" color="text.secondary" gutterBottom component="div">
          검색 대역 직접 입력(선택) - 비우면 에이전트 PC의 네트워크를 자동으로 찾습니다
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="예: 192.168.0.0/24"
            value={subnetsInput}
            onChange={(e) => setSubnetsInput(e.target.value)}
            sx={{ flex: '1 1 220px' }}
          />
          <Button
            variant="outlined"
            startIcon={starting ? <CircularProgress size={16} /> : <SearchIcon />}
            disabled={starting || isPolling(status)}
            onClick={() => startSearch(subnetsInput.trim() || undefined)}
          >
            이 대역으로 다시 검색
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EqpAgentDiscoveryDialog;
